import React, { useState, useEffect } from "react";
import {
  CognitoUserPool,
  CognitoUser,
  AuthenticationDetails,
} from "amazon-cognito-identity-js";
import api from "../api/apiClient";

const poolData = {
  UserPoolId: "eu-north-1_TTx4JU3oe",
  ClientId: "1ur0nf3sb5inbf9v5kkv14l3vk",
};
const userPool = new CognitoUserPool(poolData);

const Spinner = () => (
  <div className="flex justify-center items-center">
    <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-blue-500"></div>
  </div>
);

const AuthModal = ({ isOpen, closeModal, onSuccessfulLogin }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [username, setUsername] = useState("");
  const [isRegistering, setIsRegistering] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  
  // Glömt lösenord states
  const [isForgotPassword, setIsForgotPassword] = useState(false);
  const [isVerifyingCode, setIsVerifyingCode] = useState(false);
  const [verificationCode, setVerificationCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState("");

  // Nya fält
  const [fullName, setFullName] = useState("");
  const [address, setAddress] = useState("");
  const [postalCode, setPostalCode] = useState("");
  const [city, setCity] = useState("");
  const [country, setCountry] = useState("");

  // touch-state för inline-validering
  const [touched, setTouched] = useState({
    fullName: false,
    address: false,
    postalCode: false,
    city: false,
    country: false,
  });

  const [usernameValid, setUsernameValid] = useState(false);
  const [passwordValidation, setPasswordValidation] = useState({
    length: false,
    uppercase: false,
    number: false,
    specialChar: false,
  });
  const [emailValid, setEmailValid] = useState(false);

  // Validering för nytt lösenord vid återställning
  const [newPasswordValidation, setNewPasswordValidation] = useState({
    length: false,
    uppercase: false,
    number: false,
    specialChar: false,
  });

  useEffect(() => {
    const validatePassword = () => {
      setPasswordValidation({
        length: password.length >= 5,
        uppercase: /[A-Z]/.test(password),
        number: /\d/.test(password),
        specialChar: /[!@#$%^&*(),.?":{}|<>]/.test(password),
      });
    };
    validatePassword();
  }, [password]);

  useEffect(() => {
    const validateNewPassword = () => {
      setNewPasswordValidation({
        length: newPassword.length >= 5,
        uppercase: /[A-Z]/.test(newPassword),
        number: /\d/.test(newPassword),
        specialChar: /[!@#$%^&*(),.?":{}|<>]/.test(newPassword),
      });
    };
    validateNewPassword();
  }, [newPassword]);

  useEffect(() => {
    const validateEmail = () => {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
      setEmailValid(emailRegex.test(email));
    };
    validateEmail();
  }, [email]);

  const checkUsernameAvailability = async () => {
    if (username.length < 3) {
      setUsernameValid(false);
      return;
    }
    try {
      const { data } = await api.get(
        `/api/check-username?username=${username}`
      );
      setUsernameValid(data.available);
    } catch {
      setUsernameValid(false);
    }
  };

  useEffect(() => {
    const timeoutId = setTimeout(checkUsernameAvailability, 500);
    return () => clearTimeout(timeoutId);
  }, [username]);

  // Kontrollera att alla fält är giltiga och icke-tomma
  const allRequirementsMet = () => {
    const baseValid =
      emailValid &&
      usernameValid &&
      Object.values(passwordValidation).every((v) => v);

    const fieldsFilled =
      fullName.trim() !== "" &&
      address.trim() !== "" &&
      postalCode.trim() !== "" &&
      city.trim() !== "" &&
      country.trim() !== "";

    return baseValid && fieldsFilled;
  };

  // Glömt lösenord - skicka återställningskod
  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError("");

    try {
      const user = new CognitoUser({
        Username: forgotPasswordEmail,
        Pool: userPool,
      });

      user.forgotPassword({
        onSuccess: () => {
          setIsVerifyingCode(true);
          setError("");
        },
        onFailure: (err) => {
          setError(err.message || "Kunde inte skicka återställningskod.");
        },
      });
    } catch (err) {
      setError(err.message || "Ett fel uppstod.");
    } finally {
      setIsLoading(false);
    }
  };

  // Bekräfta återställningskod och sätt nytt lösenord
  const handleConfirmPassword = async (e) => {
    e.preventDefault();
    
    // Kontrollera att det nya lösenordet uppfyller kraven
    if (!Object.values(newPasswordValidation).every((v) => v)) {
      setError("Det nya lösenordet uppfyller inte alla krav.");
      return;
    }

    setIsLoading(true);
    setError("");

    try {
      const user = new CognitoUser({
        Username: forgotPasswordEmail,
        Pool: userPool,
      });

      user.confirmPassword(verificationCode, newPassword, {
        onSuccess: () => {
          setError("");
          // Återgå till inloggningsläge
          setIsForgotPassword(false);
          setIsVerifyingCode(false);
          setVerificationCode("");
          setNewPassword("");
          setForgotPasswordEmail("");
          // Sätt email för inloggning
          setEmail(forgotPasswordEmail);
        },
        onFailure: (err) => {
          setError(err.message || "Kunde inte återställa lösenordet.");
        },
      });
    } catch (err) {
      setError(err.message || "Ett fel uppstod.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleRegistration = async (e) => {
    e.preventDefault();
    if (!allRequirementsMet()) {
      // Markera alla som touched för att visa felmeddelanden
      setTouched({
        fullName: true,
        address: true,
        postalCode: true,
        city: true,
        country: true,
      });
      return;
    }

    setIsLoading(true);
    setError("");

    try {
      const attributeList = [{ Name: "email", Value: email }];

      userPool.signUp(
        email,
        password,
        attributeList,
        null,
        async (err, result) => {
          if (err) {
            setError(err.message || JSON.stringify(err));
            setIsLoading(false);
            return;
          }

          await api.post("/api/create-user", {
            id: result.userSub,
            email,
            username,
            credits: 200,
            full_name: fullName,
            address,
            postal_code: postalCode,
            city,
            country,
          });

          const user = new CognitoUser({ Username: email, Pool: userPool });
          const authenticationDetails = new AuthenticationDetails({
            Username: email,
            Password: password,
          });

          user.authenticateUser(authenticationDetails, {
            onSuccess: () => {
              user.getUserAttributes((err, attributes) => {
                if (err) {
                  console.error(err);
                  return;
                }
                const fullUserObject = {
                  username: user.getUsername(),
                  attributes: attributes.reduce((acc, attr) => {
                    acc[attr.getName()] = attr.getValue();
                    return acc;
                  }, {}),
                };
                onSuccessfulLogin?.(fullUserObject);
                closeModal();
              });
            },
            onFailure: (err) => {
              setError(err.message || "Automatisk inloggning misslyckades.");
            },
          });
        }
      );
    } catch (err) {
      setError(err.message || "Ett fel uppstod vid registrering.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const user = new CognitoUser({ Username: email, Pool: userPool });
      const authenticationDetails = new AuthenticationDetails({
        Username: email,
        Password: password,
      });
      user.authenticateUser(authenticationDetails, {
        onSuccess: () => {
          user.getUserAttributes((err, attributes) => {
            if (err) {
              console.error(err);
              return;
            }
            const fullUserObject = {
              username: user.getUsername(),
              attributes: attributes.reduce((acc, attr) => {
                acc[attr.getName()] = attr.getValue();
                return acc;
              }, {}),
            };
            onSuccessfulLogin?.(fullUserObject);
            closeModal();
          });
        },
        onFailure: (err) => {
          setError(err.message || "Inloggning misslyckades.");
        },
      });
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const toggleAuthMode = () => {
    setIsRegistering(!isRegistering);
    setError("");
    // Återställ glömt lösenord-lägen
    setIsForgotPassword(false);
    setIsVerifyingCode(false);
  };

  const handleForgotPasswordClick = () => {
    setIsForgotPassword(true);
    setError("");
    setForgotPasswordEmail(email); // Använd befintlig email om den finns
  };

  const handleBackToLogin = () => {
    setIsForgotPassword(false);
    setIsVerifyingCode(false);
    setError("");
    setVerificationCode("");
    setNewPassword("");
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-charcoalGray/70"
      onClick={closeModal}
    >
      <div
        className="relative w-full max-w-md bg-warmWhite rounded-lg shadow-xl p-6 my-8 sm:my-12 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Stäng-kryss */}
        <button
          onClick={closeModal}
          className="absolute top-4 right-4 text-charcoalGray hover:text-lavenderPurple"
        >
          ✕
        </button>

        <h2 className="text-2xl font-semibold text-center mb-4 text-lavenderPurple">
          {isForgotPassword
            ? isVerifyingCode
              ? "Återställ lösenord"
              : "Glömt lösenord"
            : isRegistering
            ? "Skapa konto"
            : "Logga in"}
        </h2>
        {error && <p className="text-lightCoralPink mb-4">{error}</p>}

        {isLoading ? (
          <Spinner />
        ) : isForgotPassword ? (
          isVerifyingCode ? (
            // Bekräfta återställningskod och sätt nytt lösenord
            <form onSubmit={handleConfirmPassword} className="space-y-4">
              <p className="text-sm text-charcoalGray mb-4">
                Vi har skickat en återställningskod till {forgotPasswordEmail}. 
                Ange koden och ditt nya lösenord nedan.
              </p>
              
              <input
                type="text"
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value)}
                placeholder="Återställningskod"
                required
                className="w-full p-2 border border-softGray rounded focus:outline-none focus:ring-2 focus:ring-turquoise"
              />
              
              <div>
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Nytt lösenord"
                  className="w-full p-2 border border-softGray rounded focus:outline-none focus:ring-2 focus:ring-turquoise"
                />
                <ul className="text-sm mt-1 space-y-0.5">
                  <li className={newPasswordValidation.length ? "text-green-600" : "text-red-600"}>
                    Minst 5 tecken
                  </li>
                  <li className={newPasswordValidation.uppercase ? "text-green-600" : "text-red-600"}>
                    En stor bokstav
                  </li>
                  <li className={newPasswordValidation.number ? "text-green-600" : "text-red-600"}>
                    En siffra
                  </li>
                  <li className={newPasswordValidation.specialChar ? "text-green-600" : "text-red-600"}>
                    Ett specialtecken
                  </li>
                </ul>
              </div>
              
              <button
                type="submit"
                className={`w-full p-2 rounded ${
                  Object.values(newPasswordValidation).every((v) => v) && verificationCode.length > 0
                    ? "bg-turquoise text-warmWhite hover:transform transition-transform duration-200 hover:scale-105"
                    : "bg-softGray text-charcoalGray"
                }`}
                disabled={!Object.values(newPasswordValidation).every((v) => v) || verificationCode.length === 0}
              >
                Återställ lösenord
              </button>
              
              <button
                type="button"
                onClick={handleBackToLogin}
                className="w-full text-lavenderPurple hover:underline"
              >
                Tillbaka till inloggning
              </button>
            </form>
          ) : (
            // Skicka återställningskod
            <form onSubmit={handleForgotPassword} className="space-y-4">
              <p className="text-sm text-charcoalGray mb-4">
                Ange din e-postadress så skickar vi dig en återställningskod.
              </p>
              
              <input
                type="email"
                value={forgotPasswordEmail}
                onChange={(e) => setForgotPasswordEmail(e.target.value)}
                placeholder="E-post"
                required
                className="w-full p-2 border border-softGray rounded focus:outline-none focus:ring-2 focus:ring-turquoise"
              />
              
              <button
                type="submit"
                className="w-full p-2 bg-turquoise text-warmWhite rounded hover:transform transition-transform duration-200 hover:scale-105"
              >
                Skicka återställningskod
              </button>
              
              <button
                type="button"
                onClick={handleBackToLogin}
                className="w-full text-lavenderPurple hover:underline"
              >
                Tillbaka till inloggning
              </button>
            </form>
          )
        ) : isRegistering ? (
          <form onSubmit={handleRegistration} className="space-y-4">
            {/* Användarnamn */}
            <div>
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="Användarnamn"
                className={`w-full p-2 border rounded focus:outline-none ${
                  username.length > 0
                    ? usernameValid
                      ? "border-green-500"
                      : "border-red-500"
                    : "border-softGray"
                }`}
              />
              {username.length > 0 && !usernameValid && (
                <p className="text-red-500 text-sm">
                  Användarnamnet är upptaget eller för kort (minst 3 tecken).
                </p>
              )}
            </div>

            {/* E-post */}
            <div>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="E-post"
                className={`w-full p-2 border rounded focus:outline-none ${
                  email.length > 0
                    ? emailValid
                      ? "border-green-500"
                      : "border-red-500"
                    : "border-softGray"
                }`}
              />
              {email.length > 0 && !emailValid && (
                <p className="text-red-500 text-sm">
                  Ange en giltig e-postadress.
                </p>
              )}
            </div>

            {/* Lösenord */}
            <div>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Lösenord"
                className="w-full p-2 border border-softGray rounded focus:outline-none focus:ring-2 focus:ring-turquoise"
              />
              <ul className="text-sm mt-1 space-y-0.5">
                <li className={passwordValidation.length ? "text-green-600" : "text-red-600"}>
                  Minst 5 tecken
                </li>
                <li className={passwordValidation.uppercase ? "text-green-600" : "text-red-600"}>
                  En stor bokstav
                </li>
                <li className={passwordValidation.number ? "text-green-600" : "text-red-600"}>
                  En siffra
                </li>
                <li className={passwordValidation.specialChar ? "text-green-600" : "text-red-600"}>
                  Ett specialtecken
                </li>
              </ul>
            </div>

            {/* Fullständigt namn */}
            <div>
              <input
                type="text"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                onBlur={() => setTouched((t) => ({ ...t, fullName: true }))}
                placeholder="Fullständigt namn"
                className="w-full p-2 border border-softGray rounded focus:outline-none focus:ring-2 focus:ring-turquoise"
              />
              {touched.fullName && fullName.trim() === "" && (
                <p className="text-red-500 text-sm">Fältet får ej vara tomt</p>
              )}
            </div>

            {/* Gatuadress */}
            <div>
              <input
                type="text"
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                onBlur={() => setTouched((t) => ({ ...t, address: true }))}
                placeholder="Gatuadress"
                className="w-full p-2 border border-softGray rounded focus:outline-none focus:ring-2 focus:ring-turquoise"
              />
              {touched.address && address.trim() === "" && (
                <p className="text-red-500 text-sm">Fältet får ej vara tomt</p>
              )}
            </div>

            {/* Postnummer */}
            <div>
              <input
                type="text"
                value={postalCode}
                onChange={(e) => setPostalCode(e.target.value)}
                onBlur={() => setTouched((t) => ({ ...t, postalCode: true }))}
                placeholder="Postnummer"
                className="w-full p-2 border border-softGray rounded focus:outline-none focus:ring-2 focus:ring-turquoise"
              />
              {touched.postalCode && postalCode.trim() === "" && (
                <p className="text-red-500 text-sm">Fältet får ej vara tomt</p>
              )}
            </div>

            {/* Stad */}
            <div>
              <input
                type="text"
                value={city}
                onChange={(e) => setCity(e.target.value)}
                onBlur={() => setTouched((t) => ({ ...t, city: true }))}
                placeholder="Stad"
                className="w-full p-2 border border-softGray rounded focus:outline-none focus:ring-2 focus:ring-turquoise"
              />
              {touched.city && city.trim() === "" && (
                <p className="text-red-500 text-sm">Fältet får ej vara tomt</p>
              )}
            </div>

            {/* Land */}
            <div>
              <select
                value={country}
                onChange={(e) => setCountry(e.target.value)}
                onBlur={() => setTouched((t) => ({ ...t, country: true }))}
                className="w-full p-2 border border-softGray rounded focus:outline-none focus:ring-2 focus:ring-turquoise"
              >
                <option value="">Välj land</option>
                <option value="SE">Sverige</option>
                <option value="NO">Norge</option>
                <option value="FI">Finland</option>
                <option value="DK">Danmark</option>
                <option value="US">USA</option>
                <option value="GB">Storbritannien</option>
              </select>
              {touched.country && country.trim() === "" && (
                <p className="text-red-500 text-sm">Fältet får ej vara tomt</p>
              )}
            </div>

            <button
              type="submit"
              className={`w-full p-2 rounded ${
                allRequirementsMet()
                  ? "bg-turquoise text-warmWhite hover:transform transition-transform duration-200 hover:scale-105"
                  : "bg-softGray text-charcoalGray"
              }`}
              disabled={!allRequirementsMet()}
            >
              Registrera
            </button>
          </form>
        ) : (
          <form onSubmit={handleLogin} className="space-y-4">
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="E-post"
              required
              className="w-full p-2 border border-softGray rounded focus:outline-none focus:ring-2 focus:ring-turquoise"
            />
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Lösenord"
              required
              className="w-full p-2 border border-softGray rounded focus:outline-none focus:ring-2 focus:ring-turquoise"
            />
            
            {/* Glömt lösenord länk */}
            <div className="text-right">
              <button
                type="button"
                onClick={handleForgotPasswordClick}
                className="text-sm text-lavenderPurple hover:underline"
              >
                Glömt lösenord?
              </button>
            </div>
            
            <button
              type="submit"
              className="w-full p-2 bg-turquoise text-warmWhite rounded hover:transform transition-transform duration-200 hover:scale-105"
            >
              Logga in
            </button>
          </form>
        )}

        {!isForgotPassword && (
          <button
            onClick={toggleAuthMode}
            className="mt-4 text-lavenderPurple hover:underline"
          >
            {isRegistering
              ? "Har du redan ett konto? Logga in här"
              : "Behöver du ett konto? Registrera dig här"}
          </button>
        )}
      </div>
    </div>
  );
};

export default AuthModal;